/**
 * The in-app help desk (issue #1096): what the Help button, the My requests
 * page and the admin panel share.
 *
 * The trust boundary is migration 0110 (public.help_ticket,
 * public.help_ticket_message, the help-desk-screenshots bucket): RLS decides
 * who reads what, a trigger stamps who filed a request and when it became
 * urgent, and status / urgency change only through two SECURITY DEFINER
 * functions. Everything here is for fast, friendly feedback before the
 * round trip and for wording; a caller skipping it gains nothing.
 *
 * GitHub issues and Meridian's email are opened by the droplet API
 * (src/meridian_pipeline/api/helpdesk.py), never from the browser.
 *
 * Plain .mjs so node:test imports it directly (the adminActions.mjs
 * precedent); helpDesk.d.mts types it for the React code. Data functions take
 * the supabase client first so tests hand in a chainable mock.
 */

import { BUSINESS_HOURS_NOTE, formatCentral, replyBy, RESPONSE_TARGETS } from "./replyBy.mjs";

export { BUSINESS_HOURS_NOTE, formatCentral, replyBy, RESPONSE_TARGETS };

export const STATUSES = ["open", "in_progress", "waiting_on_you", "resolved"];
export const STATUS_LABELS = Object.freeze({
  open: "Open",
  in_progress: "In progress",
  waiting_on_you: "Waiting on you",
  resolved: "Resolved",
});
export const URGENCIES = ["normal", "urgent"];
export const BODY_MAX = 5000;
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;
export const SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const SCREENSHOT_BUCKET = "help-desk-screenshots";
export const REOPEN_DAYS = 14;
export const FALLBACK_EMAIL = "aj@meridiananalytics.io";
export const FALLBACK_NOTE =
  "If the dashboard is down, email aj@meridiananalytics.io with URGENT in the subject.";

export const TICKET_COLUMNS =
  "id,number,tenant_id,requester_id,requester_name,requester_email,body,page_path,page_title," +
  "browser,screenshot_path,urgency,urgent_at,status,status_changed_at,resolved_at," +
  "first_response_at,last_message_at,last_message_role,github_issue_number,github_issue_url," +
  "notify_error,created_at";
export const MESSAGE_COLUMNS =
  "id,ticket_id,tenant_id,author_id,author_name,author_role,body,created_at";

const DAY_MS = 24 * 3600 * 1000;

/** True only when the tenant's config.yaml turns the help desk on. */
export function helpDeskEnabled(manifest) {
  return manifest?.help_desk?.enabled === true;
}

/**
 * Who sees the Help button: the tenant's users when the flag is on; Meridian
 * staff always (so the feature can be tried on the preview site before any
 * customer sees it).
 */
export function helpDeskVisible(manifest, { isStaff = false } = {}) {
  return helpDeskEnabled(manifest) || isStaff === true;
}

export function statusLabel(status) {
  return STATUS_LABELS[status] ?? "Open";
}

/** "Chrome 129 on macOS, 1440x900" from a user agent and the window size. */
export function describeBrowser(userAgent, viewport) {
  const ua = String(userAgent ?? "");
  const pick = (re) => ua.match(re)?.[1];
  let browser = "Unknown browser";
  const edge = pick(/Edg\/(\d+)/);
  const chrome = pick(/(?:Chrome|CriOS)\/(\d+)/);
  const firefox = pick(/(?:Firefox|FxiOS)\/(\d+)/);
  const safari = pick(/Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari/);
  if (edge) browser = `Edge ${edge}`;
  else if (firefox) browser = `Firefox ${firefox}`;
  else if (chrome) browser = `Chrome ${chrome}`;
  else if (safari) browser = `Safari ${safari}`;
  let os = "";
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X|Macintosh/.test(ua)) os = "macOS";
  else if (/CrOS/.test(ua)) os = "ChromeOS";
  else if (/Linux/.test(ua)) os = "Linux";
  const size =
    viewport && viewport.width > 0 && viewport.height > 0
      ? `${Math.round(viewport.width)}x${Math.round(viewport.height)}`
      : "";
  return [os ? `${browser} on ${os}` : browser, size].filter(Boolean).join(", ").slice(0, 500);
}

/** The first non-blank line, whitespace collapsed (the issue title uses the same rule). */
export function firstLine(text) {
  for (const line of String(text ?? "").split(/\r?\n/)) {
    const collapsed = line.trim().split(/\s+/).join(" ");
    if (collapsed) return collapsed;
  }
  return "";
}

/** Field errors for the form, before anything is sent. */
export function validateRequest({ body, urgency, screenshot } = {}) {
  const errors = {};
  const text = String(body ?? "").trim();
  if (!text) errors.body = "Tell us what is wrong or what you need.";
  else if (text.length > BODY_MAX) errors.body = `Keep it under ${BODY_MAX} characters.`;
  if (urgency != null && !URGENCIES.includes(urgency)) errors.urgency = "Pick Normal or Urgent.";
  if (screenshot) {
    if (!SCREENSHOT_TYPES.includes(screenshot.type)) {
      errors.screenshot = "Attach a PNG, JPEG, WebP or GIF image.";
    } else if (screenshot.size > SCREENSHOT_MAX_BYTES) {
      errors.screenshot = "Attach an image under 5 MB.";
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** The row the dashboard inserts: exactly the columns migration 0110 grants. */
export function buildTicketInsert({
  tenantId,
  body,
  urgency,
  pagePath,
  pageTitle,
  browser,
  screenshotPath,
}) {
  return {
    tenant_id: tenantId,
    body: String(body ?? "").trim(),
    urgency: urgency === "urgent" ? "urgent" : "normal",
    page_path: pagePath ? String(pagePath).slice(0, 500) : null,
    page_title: pageTitle ? String(pageTitle).slice(0, 200) : null,
    browser: browser ? String(browser).slice(0, 500) : null,
    screenshot_path: screenshotPath ?? null,
  };
}

const EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
};

/** <tenant>/<user>/<id>.<ext>: the only names the bucket policy accepts from this user. */
export function screenshotObjectPath(tenantId, userId, fileType, id) {
  const ext = EXTENSIONS[fileType] ?? "png";
  return `${tenantId}/${userId}/${id}.${ext}`;
}

function ms(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Whether the viewer may add to the conversation. A Resolved request reopens
 * on a reply within 14 days of resolution; after that the requester opens a
 * new one.
 */
export function replyState(ticket, now = Date.now()) {
  if (ticket?.status !== "resolved") return { canReply: true, reopens: false, reason: null };
  const resolved = ms(ticket.resolved_at);
  if (resolved != null && now - resolved > REOPEN_DAYS * DAY_MS) {
    return {
      canReply: false,
      reopens: false,
      reason: "Resolved more than 14 days ago. Open a new request if you still need help.",
    };
  }
  return { canReply: true, reopens: true, reason: null };
}

/** When Meridian owes a reply on this request (null once replied or resolved). */
export function ticketReplyBy(ticket) {
  if (!ticket || ticket.status === "resolved" || ticket.first_response_at) return null;
  return replyBy(ticket.created_at, ticket.urgency, ticket.urgent_at);
}

/** The one line under a request: its response target, or what happened instead. */
export function targetLine(ticket, now = Date.now()) {
  if (!ticket) return "";
  if (ticket.status === "resolved") {
    return ticket.resolved_at ? `Resolved ${formatCentral(ticket.resolved_at)}` : "Resolved";
  }
  if (ticket.first_response_at)
    return `Meridian replied ${formatCentral(ticket.first_response_at)}`;
  const due = ticketReplyBy(ticket);
  if (!due) return "";
  const when = formatCentral(due);
  return due.getTime() < now ? `Reply was due ${when}` : `Expected reply by ${when}`;
}

const ERRORS = {
  help_ticket_rate_limited:
    "You have sent 10 requests in the last hour. Add to one of your open requests instead.",
  help_ticket_closed: "This request was resolved more than 14 days ago. Open a new request.",
  help_ticket_owner_only: "Only your company's administrator can mark a request urgent.",
  help_ticket_staff_only: "Only Meridian can change the status.",
  help_ticket_not_found: "That request is not available.",
  help_ticket_screenshot_not_yours: "The screenshot did not upload. Attach it again.",
};

/** A sentence for the person, never a raw database error. */
export function friendlyError(error) {
  const text = `${error?.code ?? ""} ${error?.message ?? ""} ${error?.details ?? ""} ${error?.hint ?? ""}`;
  for (const [code, message] of Object.entries(ERRORS)) {
    if (text.includes(code)) return message;
  }
  if (/row-level security|permission denied|42501/.test(text)) {
    return "You do not have access to do that.";
  }
  if (/does not exist|Could not find the table|PGRST205|42P01/.test(text)) {
    return `The help desk is not switched on yet. ${FALLBACK_NOTE}`;
  }
  return `Something went wrong. Try again. ${FALLBACK_NOTE}`;
}

/* ---- data (supabase client first) ---------------------------------------- */

export async function uploadScreenshot(supabase, { tenantId, userId, file, id }) {
  const path = screenshotObjectPath(tenantId, userId, file.type, id);
  // Storage, not a table: the object name starts with the tenant_id, and the
  // 0110 bucket policy accepts only a visible tenant_id and the user's own id.
  const { error } = await supabase.storage
    .from(SCREENSHOT_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) return { path: null, error };
  return { path, error: null };
}

export async function createTicket(supabase, insert) {
  const { data, error } = await supabase
    .from("help_ticket")
    .insert(insert)
    .select(TICKET_COLUMNS)
    .single();
  return { ticket: data ?? null, error: error ?? null };
}

/** A tenant's requests the session may read; `mineOnly` narrows to the requester's own. */
export async function listTickets(supabase, { tenantId, userId, mineOnly }) {
  let query = supabase.from("help_ticket").select(TICKET_COLUMNS).eq("tenant_id", tenantId);
  if (mineOnly && userId) query = query.eq("requester_id", userId);
  const { data, error } = await query.order("created_at", { ascending: false });
  return { tickets: data ?? [], error: error ?? null };
}

/** Staff: every tenant's requests (RLS: staff read all), newest first, with the customer. */
export async function listAllTickets(supabase, { status } = {}) {
  // tenant_id is selected and the customer embedded; staff-only page.
  let query = supabase.from("help_ticket").select(`${TICKET_COLUMNS},tenant:tenants(slug,name)`);
  if (status && STATUSES.includes(status)) query = query.eq("status", status);
  const { data, error } = await query.order("created_at", { ascending: false }).limit(500);
  return { tickets: data ?? [], error: error ?? null };
}

export async function listMessages(supabase, { tenantId, ticketId }) {
  const { data, error } = await supabase
    .from("help_ticket_message")
    .select(MESSAGE_COLUMNS)
    .eq("tenant_id", tenantId)
    .eq("ticket_id", ticketId)
    .order("created_at", { ascending: true });
  return { messages: data ?? [], error: error ?? null };
}

/** Add to the conversation. author and tenant_id are stamped by the 0110 trigger. */
export async function addReply(supabase, { ticketId, body }) {
  const text = String(body ?? "").trim();
  if (!text) return { message: null, error: { message: "empty" } };
  // tenant_id is not sent: the 0110 trigger copies it from the ticket, and the
  // insert policy accepts only a ticket the session can already read.
  const { data, error } = await supabase
    .from("help_ticket_message")
    .insert({ ticket_id: ticketId, body: text })
    .select(MESSAGE_COLUMNS)
    .single();
  return { message: data ?? null, error: error ?? null };
}

export async function markUrgent(supabase, ticketId) {
  const { data, error } = await supabase.rpc("help_ticket_mark_urgent", { p_ticket: ticketId });
  return { ticket: data ?? null, error: error ?? null };
}

export async function setStatus(supabase, ticketId, status) {
  const { data, error } = await supabase.rpc("help_ticket_set_status", {
    p_ticket: ticketId,
    p_status: status,
  });
  return { ticket: data ?? null, error: error ?? null };
}

/** Whether the session is the company's administrator (an active owner). */
export async function isCompanyAdmin(supabase, tenantId) {
  const { data, error } = await supabase.rpc("help_desk_is_owner", { p_tenant: tenantId });
  return !error && data === true;
}

/** A five-minute link to a screenshot the session may read. */
export async function screenshotUrl(supabase, path) {
  if (!path) return null;
  // Storage: the path is the ticket's own (tenant_id/user/file); the 0110 read
  // policy signs it only when the session can read that ticket.
  const { data, error } = await supabase.storage.from(SCREENSHOT_BUCKET).createSignedUrl(path, 300);
  return error ? null : (data?.signedUrl ?? null);
}
